import {
    DISCUSSION_TOPICS,
    type DiscussionComment,
    type DiscussionCommentThread,
    type DiscussionTopic,
    type DiscussionTopicId
} from '../types/Discussion.ts'
import { slugify } from './slug.ts'

/** Hot ranking starts counting from here; any fixed point in the past works. */
const HOT_EPOCH_SECONDS = Date.UTC(2026, 0, 1) / 1000
/**
 * Ten times the activity keeps a thread level with one posted this much later.
 * Reddit uses 12.5 h; a young, slower board needs threads to stay visible longer.
 */
const HOT_DECAY_SECONDS = 2 * 24 * 60 * 60

export function isDiscussionTopic(value: unknown): value is DiscussionTopicId {
    return DISCUSSION_TOPICS.some((topic) => topic.id === value)
}

export function getDiscussionTopic(id: unknown): DiscussionTopic | undefined {
    return DISCUSSION_TOPICS.find((topic) => topic.id === id)
}

/**
 * Reddit-style hot score. Creation time is baked in, so the score never needs a periodic
 * recompute: newer threads simply start higher. Recalculate it on every vote and comment.
 */
export function discussionHotScore(upvotes: number, commentCount: number, createdAtMillis: number): number {
    const activity = Math.max(1, upvotes + commentCount)
    return Math.log10(activity) + (createdAtMillis / 1000 - HOT_EPOCH_SECONDS) / HOT_DECAY_SECONDS
}

/** `suffix` keeps slugs unique for repeated titles; it also becomes the document ID. */
export function buildDiscussionSlug(title: string, suffix: string): string {
    const base =
        slugify(title)
            .slice(0, 80)
            .replace(/^-+|-+$/g, '') || 'diskusia'
    return `${base}-${suffix}`
}

export function discussionExcerpt(body: string, maxLength = 200): string {
    const text = body.replace(/\s+/g, ' ').trim()
    if (text.length <= maxLength) return text
    return `${text.slice(0, maxLength).replace(/\s+\S*$/, '')}…`
}

/** "Jana Nováková" → "Jana N." Threads are public and indexed, so full surnames stay off them. */
export function discussionAuthorName(firstName: unknown, lastName: unknown): string {
    const first = typeof firstName === 'string' ? firstName.trim() : ''
    const last = typeof lastName === 'string' ? lastName.trim() : ''
    const name = [first, last ? `${last.charAt(0).toUpperCase()}.` : ''].filter(Boolean).join(' ')
    return name.slice(0, 80) || 'Používateľ GROWNi'
}

/**
 * "pred 3 hodinami", "včera", "12. 3. 2026". Hour-level at most, so SSR and hydration
 * rarely disagree on the text.
 */
export function discussionRelativeTime(iso: string, now = Date.now()): string {
    const date = new Date(iso)
    const hours = Math.floor((now - date.getTime()) / 3_600_000)
    if (Number.isNaN(hours)) return ''
    if (hours < 1) return 'pred chvíľou'
    if (hours < 24) return hours === 1 ? 'pred hodinou' : `pred ${hours} hodinami`
    const days = Math.floor(hours / 24)
    if (days === 1) return 'včera'
    if (days < 7) return `pred ${days} dňami`
    return date.toLocaleDateString('sk-SK', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        timeZone: 'Europe/Bratislava'
    })
}

/** Slovak plural: 1 komentár, 2–4 komentáre, 5+ komentárov. */
export function discussionCommentLabel(count: number): string {
    if (count === 1) return '1 komentár'
    if (count >= 2 && count <= 4) return `${count} komentáre`
    return `${count} komentárov`
}

/**
 * Groups comments into top-level threads with their replies, both oldest first.
 * Replies whose parent is missing are shown as top-level rather than dropped.
 */
export function buildDiscussionCommentThreads(comments: DiscussionComment[]): DiscussionCommentThread[] {
    const byCreation = [...comments].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id))
    const topLevelIds = new Set(byCreation.filter((comment) => !comment.parentId).map((comment) => comment.id))
    const threads = new Map<string, DiscussionCommentThread>()
    for (const comment of byCreation) {
        if (comment.parentId && topLevelIds.has(comment.parentId)) {
            threads.get(comment.parentId)?.replies.push(comment)
        } else {
            threads.set(comment.id, { comment, replies: [] })
        }
    }
    return [...threads.values()]
}
